import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import certificatesService from '../../services/certificatesService';
import studentsService from '../../services/studentsService';
import { DownloadSimple, MagnifyingGlass, X, CheckCircle } from '@phosphor-icons/react';

const CERT_TYPES = [
  { value: 'leaving', label: 'Leaving Certificate' },
  { value: 'transfer', label: 'Transfer Certificate' },
  { value: 'achievement', label: 'Certificate of Achievement' },
];

function CertificateIssuancePage() {
  const [loading, setLoading] = useState(true);
  const [issuances, setIssuances] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [certType, setCertType] = useState('leaving');
  const [remarks, setRemarks] = useState('');
  const [generating, setGenerating] = useState(false);
  const [reprintingId, setReprintingId] = useState(null);
  const [verifyNumber, setVerifyNumber] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [issuancesData, studentsData] = await Promise.all([
        certificatesService.listIssuances(),
        studentsService.listStudents().catch(() => []),
      ]);
      setIssuances(Array.isArray(issuancesData) ? issuancesData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : studentsData?.items || studentsData?.students || []);
    } catch (error) {
      toast.error('Failed to load certificate issuances');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (id) => {
    const student = students.find((s) => s.id === id);
    return student ? `${student.first_name} ${student.last_name}` : id;
  };

  const downloadBlobResponse = async (response, filename) => {
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);
  };

  const handleGenerate = async () => {
    if (!studentId) {
      toast.error('Select a student');
      return;
    }
    setGenerating(true);
    try {
      const response = await certificatesService.generateCertificate(studentId, certType, { remarks: remarks || null });
      await downloadBlobResponse(response, `certificate_${certType}_${getStudentName(studentId).replace(/\s+/g, '_')}.pdf`);
      toast.success('Certificate generated');
      setRemarks('');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  const handleReprint = async (issuance) => {
    setReprintingId(issuance.id);
    try {
      const response = await certificatesService.reprintCertificate(issuance.id);
      await downloadBlobResponse(response, `certificate_${issuance.certificate_type}_reprint.pdf`);
    } catch (error) {
      toast.error(error.message || 'Failed to reprint certificate');
    } finally {
      setReprintingId(null);
    }
  };

  const handleVerify = async () => {
    if (!verifyNumber.trim()) {
      toast.error('Enter a certificate number');
      return;
    }
    setVerifying(true);
    setVerifyResult(null);
    try {
      const result = await certificatesService.verify(verifyNumber.trim());
      setVerifyResult(result);
    } catch (error) {
      toast.error(error.message || 'Certificate not found');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading certificate issuances...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Certificates — Issuance</h1>
            <p>Generate leaving, transfer, and achievement certificates</p>
          </div>
        </div>

        <div className="form-card" style={{ marginBottom: 20, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Generate Certificate</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
              <label>Student</label>
              <select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
                <option value="">Select Student</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
              <label>Certificate Type</label>
              <select value={certType} onChange={(e) => setCertType(e.target.value)}>
                {CERT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 220 }}>
              <label>Remarks (optional)</label>
              <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g., Completed studies with distinction" />
            </div>
            <button className="btn btn-primary" onClick={handleGenerate} disabled={generating}>
              <DownloadSimple size={18} />
              {generating ? 'Generating...' : 'Generate & Download'}
            </button>
          </div>
        </div>

        <div className="form-card" style={{ marginBottom: 20, padding: 16 }}>
          <h3 style={{ marginTop: 0 }}>Verify a Certificate</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ margin: 0, minWidth: 260 }}>
              <label>Certificate Number</label>
              <input
                type="text"
                value={verifyNumber}
                onChange={(e) => setVerifyNumber(e.target.value)}
                placeholder="e.g., ABCD1234-LEA-1A2B3C4D"
              />
            </div>
            <button className="btn btn-primary" onClick={handleVerify} disabled={verifying}>
              <MagnifyingGlass size={18} />
              {verifying ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </div>

        <div className="table-container">
          {issuances.length === 0 ? (
            <div className="empty-state">
              <p>No certificates issued yet</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Certificate #</th>
                  <th>Student</th>
                  <th>Type</th>
                  <th>Issue Date</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {issuances.map((issuance) => (
                  <tr key={issuance.id}>
                    <td>{issuance.certificate_number}</td>
                    <td>{getStudentName(issuance.student_id)}</td>
                    <td className="text-capitalize">{issuance.certificate_type}</td>
                    <td>{issuance.issue_date}</td>
                    <td>{issuance.remarks || '-'}</td>
                    <td className="action-buttons">
                      <button
                        className="btn-icon btn-primary"
                        title="Reprint"
                        onClick={() => handleReprint(issuance)}
                        disabled={reprintingId === issuance.id}
                      >
                        <DownloadSimple size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {verifyResult && (
        <div className="modal-overlay" onClick={() => setVerifyResult(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h2>Certificate Verified</h2>
              <button type="button" className="btn-close" onClick={() => setVerifyResult(null)} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '8px 0 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, color: '#16a34a' }}>
                <CheckCircle size={24} weight="fill" />
                <strong>Valid Certificate</strong>
              </div>
              <table className="info-table" style={{ width: '100%' }}>
                <tbody>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px 4px 0' }}>Certificate #</td><td>{verifyResult.certificate_number}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px 4px 0' }}>Type</td><td className="text-capitalize">{verifyResult.certificate_type}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px 4px 0' }}>Student</td><td>{verifyResult.student_name}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px 4px 0' }}>Issue Date</td><td>{verifyResult.issue_date}</td></tr>
                  <tr><td style={{ fontWeight: 600, padding: '4px 8px 4px 0' }}>Issued By</td><td>{verifyResult.issued_by || '-'}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default CertificateIssuancePage;
